import Vue from 'vue';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { Component } from 'vue-property-decorator';
import { Dossier, Credentials, Attachment, FolderTreeNode } from '../interfaces/interfaces';
import moment from 'moment';
import nestedAppAuthenticationManager from '../nested-app-authentication-manager';
import sleep from "@/utils/sleep";

@Component
export default class LysiasApiMixin extends Vue
{
    private LYSIAS5MAILCATEGORY = "Lysias5Mail";

    constructor()
    {
        super();
    }

    mounted()
    {
        if(this.sharedAxiosInstance) {
            return;
        }

        console.log('In mounted - this.$router:', this.$router);

        const sharedAxiosInstance = axios.create();
        sharedAxiosInstance!.interceptors.response.use(response => {
            return response;
        }, error => {
            if (error.response.status === 401) {
                this.$store.dispatch('setErrorMessage', 'Identifiants invalides');

                this.$store.commit('setPassword', '');
                this.$store.commit('setIsLoggedIn', { nom: this.$store.state.currentCabinet.nom, isLoggedIn: false });
                this.$store.dispatch('newLogin');

                return Promise.reject(error);
            }

            return Promise.reject(error);
        });

        this.$store.commit('setSharedAxiosInstance', sharedAxiosInstance);
    }

    get sharedAxiosInstance(): AxiosInstance|null
    {
        return this.$store.state.sharedAxiosInstance;
    }

	async login(username: string, password: string): Promise<boolean>
	{
		try
		{
			const currentCabinet = this.$store.state.currentCabinet;
            if(!currentCabinet)
            {
                return false;
            }

            const url = this.$store.getters.normalizedCabinetUrl + '/api/login/authenticate';
            const response = await this.sharedAxiosInstance!.get(url, {
                auth: {
                    username: username,
                    password: password,
                },
                validateStatus: (status) =>
                {
                    return status == 200;
                }
            });

            this.$store.commit('setCapabilities', response.data['capabilities']);
            this.$store.commit('setHasAI', 'has_ai' in response.data ? response.data['has_ai'] : false);
            this.$store.commit('setIsLoggedIn', { nom: this.$store.state.currentCabinet.nom, isLoggedIn: true });

            return true;
		}
		catch(e)
		{
            this.$store.commit('setIsLoggedIn', { nom: this.$store.state.currentCabinet.nom, isLoggedIn: false });
            console.error(e);
			return false;
		}

	}

	async searchDossier(term: string, isExpeditor: string, withEmail: string|null, withArchived: boolean): Promise<Dossier[]>
        {
            if(term.trim().length == 0 && withEmail == null)
            {
                return [];
            }

            const params: {[k: string]: any} = {
                withArchived,
            };

            if(withEmail != null)
            {
                params['isExpeditor'] = isExpeditor;
                params['withEmail'] = withEmail;
            }


            const url = this.$store.getters.normalizedCabinetUrl + '/api/dossier/search/' + term;
            const response = await this.sharedAxiosInstance!.get(url, {
                auth: {
                    username: this.getCredentials().username,
                    password: this.getCredentials().password,
                },
                validateStatus: (status) =>
                {
                    return status == 200;
                },
                params: params,
            });

            const dossiers: Dossier[] = response.data.map((dossier: any) => {
                const nom = dossier.dosNom ?? dossier.nom;
                const numero = dossier.dosNumero ?? dossier.numero;
                return new Dossier(dossier.id, nom, numero);
            });

            return dossiers;
        }

    async getDossiersWithEmail(isExpeditor: string, withEmail: string|null, withArchived: boolean): Promise<Dossier[]>
    {
        const params = {
            isExpeditor,
            withEmail,
            withArchived
        };
        const url = this.$store.getters.normalizedCabinetUrl + '/api/dossier/all';
        const response = await this.sharedAxiosInstance!.get(url, {
            auth: {
                username: this.getCredentials().username,
                password: this.getCredentials().password,
            },
            validateStatus: (status) =>
            {
                return status == 200;
            },
            params: params,
        });

        const dossiers: Dossier[] = response.data.map((dossier: any) => {
            return new Dossier(dossier.id, dossier.nom, dossier.numero)
        });

        return dossiers;
    }

	async getCloudSubfolders(dossier: Dossier, subfolderPath?: string)
	{
        try
        {
            const params = new URLSearchParams();
            if(subfolderPath)
            {
                params.append('subfolder_path', subfolderPath);
            }

            const url = this.$store.getters.normalizedCabinetUrl + '/api/mail/get-cloud-subfolders/' + dossier.id;
            const response = await this.sharedAxiosInstance!.post(url, params, {
                auth: {
                    username: this.getCredentials().username,
                    password: this.getCredentials().password,
                },
                validateStatus: (status) =>
                {
                    return status == 200;
                }
            });

            const repertoires: string[] = response.data;
            repertoires.sort();

            return repertoires;
        }
        catch(e)
        {
            return [];
        }
	}

	async getCloudSubfoldersHierarchy(dossier: Dossier)
	{
        try
        {
            const url = this.$store.getters.normalizedCabinetUrl + '/api/mail/cloud-folder-hierarchy/' + dossier.id;
            const response = await this.sharedAxiosInstance!.get(url, {
                auth: {
                    username: this.getCredentials().username,
                    password: this.getCredentials().password,
                },
                validateStatus: (status) =>
                {
                    return status == 200;
                }
            });

            const repertoiresHierarchy: string[] = response.data;

            return repertoiresHierarchy;
        }
        catch(e)
        {
            return [];
        }
	}
	async addMail(dossier: Dossier, mail: Office.MessageRead, mailSavePath: string, selectedAttachments: Attachment[], mailFilename: string)
	{
        if(mailSavePath.length == 0)
        {
            mailSavePath = "/";
        }

        mailFilename += ".eml";
        const emlFileBlob = await this.getEml(mail);
        const body = await this.getBody(mail);
        const setIsIncomingMail = mail.from.emailAddress === Office.context.mailbox.userProfile.emailAddress ? "0" : "1";
        const to = mail.to.map((emailAdressDetails: Office.EmailAddressDetails) => {
            return emailAdressDetails.emailAddress;
        }).join(',');

        const entryId = await this.getEntryIdFromItemId(mail.itemId);
        const params = new FormData();
        params.set('id_dossier', dossier.id);
        params.set('internet_message_id', mail.internetMessageId);
        params.set('conversation_id', mail.conversationId);
        params.set('entry_id', entryId);
        params.set('from', mail.from.emailAddress);
        params.set('to', to);
        params.set('subject', mail.subject);
        params.set('body', body);
        params.set('date', moment(mail.dateTimeCreated).format('DD/MM/YYYY HH:mm:ss'));
        params.set('is_incoming_mail', setIsIncomingMail);
        params.set('mail_save_path', mailSavePath);
        params.set('attachments_save_path', mailSavePath);

        selectedAttachments.forEach((attachment, index) => {
            const blob = this.base64toBlob(attachment.mimeContent!, attachment.contentType!);

            params.append(`file_${index}`, blob, attachment!.name);

            if(this.$store.state.capabilities.includes('per_attachment_save_path')) {
                params.append('attachments_save_path[]', attachment.savePath!);
            }
        });

        params.append('emlFilename', emlFileBlob, mailFilename);

        const url = this.$store.getters.normalizedCabinetUrl + '/api/mail/add';
        await this.sharedAxiosInstance!.post(url, params, {
            auth: {
                username: this.getCredentials().username,
                password: this.getCredentials().password,
            },
            validateStatus: (status) =>
            {
                return status == 200 || status == 201;
            },
            headers: {'Content-Type': 'multipart/form-data' }
        });

      this.addLysias5MailCategory(mail);
	}

	async removeMail(mail: Office.MessageRead, dossier: Dossier|null)
	{
        const entryId = await this.getEntryIdFromItemId(mail.itemId);
        const url = this.$store.getters.normalizedCabinetUrl + `/api/mail/${entryId}`;
        const options: AxiosRequestConfig = {
            auth: {
                username: this.getCredentials().username,
                password: this.getCredentials().password,
            },
            validateStatus: (status) =>
            {
                return status == 200;
            },
        };

        if(dossier) {
            options.params = { dossierId: dossier.id };
        }

        await this.sharedAxiosInstance!.delete(url, options);

        if(this.$store.state.linkedDossiers.length <= 1) {
            this.removeLysias5MailCategory(mail);
        }
	}

	async saveAttachmentsOnly(dossier: Dossier, mail: Office.MessageRead, savePath: string, attachments: Attachment[])
	{
        const params = new FormData();
        params.append('save_path', savePath);

        attachments.forEach((attachment, index) => {
            const blob = this.base64toBlob(attachment.mimeContent!, attachment.contentType!);

            params.append(`file_${index}`, blob, attachment.name);
            params.append('save_path[]', attachment.savePath!);
        });

        const url = this.$store.getters.normalizedCabinetUrl + '/api/mail/attachments/add/' + dossier.id;
        const response = await this.sharedAxiosInstance!.post(url, params, {
            auth: {
                username: this.getCredentials().username,
                password: this.getCredentials().password,
            },
            validateStatus: (status) =>
            {
                return status == 200 || status == 201;
            },
            headers: {'Content-Type': 'multipart/form-data' }
        });
	}

    private async getEml(mail: Office.MessageRead): Promise<Blob>
    {
        return this.getEmlFromGraphApi(mail);
    }

    private async getEmlFromGraphApi(mail: Office.MessageRead)
    {
        const itemId = this.getItemRestId(mail);
        const accessToken = await nestedAppAuthenticationManager.getToken(this.$store, this.$router);
        const url = `https://graph.microsoft.com/v1.0/me/messages/${itemId}/$value`;
        const response = await this.sharedAxiosInstance!.get(url, {
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            validateStatus: (status) =>
            {
                return status == 200;
            },
        });

        const mimeContent = response.data;
        const blob = this.binaryToBlob(mimeContent, 'message/rfc822 eml');

        return blob;
    }

    private async getEntryIdFromItemId(itemId: string): Promise<string>
    {
        const accessToken = await nestedAppAuthenticationManager.getToken(this.$store, this.$router);
        const url = 'https://graph.microsoft.com/v1.0/me/translateExchangeIds'; // Replace with your actual endpoint
        const params = {
            inputIds: [
                itemId,
            ],
            sourceIdType: "ewsId",
            targetIdType: "entryId"
        };

        const response = await this.sharedAxiosInstance!.post(url, params, {
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            validateStatus: (status) =>
            {
                return status == 200;
            },
        });

        const data = response.data;

        return data.value[0].targetId;
    }

    private async getBody(mail: Office.MessageRead): Promise<string>
    {
        return new Promise((resolve, reject) => {
            mail.body.getAsync("html", (asyncResult) => {
                if(asyncResult.status == Office.AsyncResultStatus.Failed)
                {
                    reject();
                    return;
                }

                resolve(asyncResult.value);
            });
        });
    }

    async getAttachments(mail: Office.MessageRead, selectedAttachments?: Attachment[]): Promise<Attachment[]>
    {
        return await this.getAttachmentsFromGraphApi(mail, selectedAttachments);
    }

    private async getAttachmentsFromGraphApi(mail: Office.MessageRead, selectedAttachments?: Attachment[])
    {
        const itemId = this.getItemRestId(mail);
        const token  = await nestedAppAuthenticationManager.getToken(this.$store, this.$router);
        const baseUrl = `https://graph.microsoft.com/v1.0/me/messages/${itemId}/attachments`;

            const response = await this.sharedAxiosInstance!.get(baseUrl, {
            headers:       { Authorization: `Bearer ${token}` },
            validateStatus: status => status === 200,
        });

        const graphItems = (response.data.value as any[])
        .filter(att => !this.isQuickSignatureCheck(att));

        let requestDelay = 0;

        const attachments = await Promise.all(
            graphItems.map(async (att, index) => {
                    // Cas 1 : fichier classique (image, pdf, etc.)
                    if (att['@odata.type'] === '#microsoft.graph.fileAttachment') {
                        return {
                            id:          index,
                            name:        att.name,
                            contentType: att.contentType,
                            mimeContent: att.contentBytes,
                            savePath:    null,
                        }
                    }

                    if (att['@odata.type'] === '#microsoft.graph.itemAttachment'
                        && att.contentType === 'message/rfc822'
                    ) {
                        requestDelay += 200;

                        // On n'envoie pas les requetes immédiatement pour éviter
                        // de se faire rate-limit par microsoft
                        return sleep(requestDelay)
                            .then(() =>
                                this.sharedAxiosInstance!.get(
                                    `${baseUrl}/${encodeURIComponent(att.id)}/$value`,
                                    {
                                        headers: {Authorization: `Bearer ${token}`},
                                        responseType: 'arraybuffer',
                                        validateStatus: status => status === 200,
                                    }
                                )
                            )
                            .then((rawResp) => {
                                const uint8 = new Uint8Array(rawResp.data);
                                let binary = '';

                                for (let i = 0; i < uint8.length; i++) {
                                    binary += String.fromCharCode(uint8[i]);
                                }

                                const base64 = btoa(binary);

                                return {
                                    id:          index,
                                    name:        att.name.endsWith('.eml') ? att.name : `${att.name}.eml`,
                                    contentType: 'message/rfc822',
                                    mimeContent: base64,
                                    savePath:    null,
                                };
                            })
                    }

                    console.warn('Attachment type non géré', att);
                    return null;
            })
        );

        const filtered = attachments.filter(a => a !== null) as Attachment[];

        if (selectedAttachments && selectedAttachments.length) {
            return filtered.filter(a => selectedAttachments.some(sa => sa.id === a.id));
        }

        return filtered;
    }

    async isMailLinkedToOneDossier(mail: Office.MessageRead): Promise<boolean>
    {
        const entryId = await this.getEntryIdFromItemId(mail.itemId);
        const url = this.$store.getters.normalizedCabinetUrl + '/api/mail/dossier';
        const params = new URLSearchParams();
        params.append('entry_id', entryId);
        params.append('internet_message_id', mail.internetMessageId);
        const response = await this.sharedAxiosInstance!.get(url, {
            auth: {
                username: this.getCredentials().username,
                password: this.getCredentials().password,
            },
            params: params,
            validateStatus: (status) =>
            {
                return status == 200 || status == 404;
            }
        });

        const data = response.data;
        const isLinked = response.status == 200;
        if(isLinked)
        {
            const dossier = new Dossier(data.id, data.dosNom, data.dosNumero);
            this.$store.commit('setCurrentDossier', dossier);
            this.$store.commit('setLinkedDossiers', [dossier]);
        }
        else {
            this.$store.commit('setLinkedDossiers', []);
        }

        return isLinked;
    }

    async isMailLinkedToMultipleDossier(mail: Office.MessageRead): Promise<boolean>
    {
        const entryId = await this.getEntryIdFromItemId(mail.itemId);
        const url = this.$store.getters.normalizedCabinetUrl + '/api/mail/dossiers';
        const params = new URLSearchParams();
        params.append('entry_id', entryId);
        params.append('internet_message_id', mail.internetMessageId);
        const response = await this.sharedAxiosInstance!.get(url, {
            auth: {
                username: this.getCredentials().username,
                password: this.getCredentials().password,
            },
            params: params,
            validateStatus: (status) =>
            {
                return status == 200 || status == 404;
            }
        });

        const data = response.data;
        const isLinked = response.status == 200;
        if(isLinked)
        {
            const dossiers = data.map((d:any) => new Dossier(d.id, d.dosNom, d.dosNumero));
            this.$store.commit('setLinkedDossiers', dossiers);
        }
        else {
            this.$store.commit('setLinkedDossiers', []);
        }

        return isLinked;
    }

	private getCredentials(): Credentials
	{
		return this.$store.state.currentCabinet.credentials;
	}

    public base64toBlob(base64: string, mimeType: string)
    {
        // convert base64 to raw binary data held in a string
        // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
        const byteString = atob(base64);

        return this.binaryToBlob(byteString, mimeType);
    }

    private binaryToBlob(byteString: string, mimeType: string)
    {
        // write the bytes of the string to an ArrayBuffer
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++)
        {
            ia[i] = byteString.charCodeAt(i);
        }

        // write the ArrayBuffer to a blob, and you're done
        const blob = new Blob([ab], { type: mimeType });
        return blob;
    }

    private isRestSupported()
    {
        return Office.context.requirements.isSetSupported('Mailbox', '1.5');
    }

    private getItemRestId(mail: Office.MessageRead)
    {
        if (Office.context.mailbox.diagnostics.hostName === 'OutlookIOS')
        {
            // itemId is already REST-formatted.
            return mail.itemId;
        }
        else
        {
            // Convert to an item ID for API v2.0.
            return Office.context.mailbox.convertToRestId(
                mail.itemId,
                Office.MailboxEnums.RestVersion.v2_0
            );
        }
    }

    private addLysias5MailCategory(mail: Office.MessageRead)
    {
        this.addLysias5MailMasterCategory();

        const categoriesToAdd = [this.LYSIAS5MAILCATEGORY];

        mail.categories.addAsync(categoriesToAdd, (asyncResult) =>
        {
            if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                console.log("Successfully added categories");
            } else {
                console.log("categories.addAsync call failed with error: " + asyncResult.error.message);
            }
        });
    }

    private addLysias5MailMasterCategory()
    {
        Office.context.mailbox.masterCategories.getAsync((asyncResult) =>
        {
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                console.log("Action failed with error: " + asyncResult.error.message);
            } else {
                const masterCategories = asyncResult.value;
                console.log("Master categories:");
                for(const category of masterCategories)
                {
                    if(category.displayName == this.LYSIAS5MAILCATEGORY)
                    {
                        return true;
                    }
                }

                const masterCategoriesToAdd = [
                    {
                        "displayName": this.LYSIAS5MAILCATEGORY,
                        "color": Office.MailboxEnums.CategoryColor.Preset1 // Orange
                    }
                ];

                Office.context.mailbox.masterCategories.addAsync(masterCategoriesToAdd, (asyncResult) =>
                {
                    if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                        console.log("Successfully added categories to master list");
                    } else {
                        console.log("masterCategories.addAsync call failed with error: " + asyncResult.error.message);
                    }
                });
            }
        });
    }

    private removeLysias5MailCategory(mail: Office.MessageRead)
    {
        const categoriesToRemove = [this.LYSIAS5MAILCATEGORY];

        mail.categories.removeAsync(categoriesToRemove, function (asyncResult) {
            if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                console.log("Successfully removed categories");
            } else {
                console.log("categories.removeAsync call failed with error: " + asyncResult.error.message);
            }
        });
    }

    async processOcr(arrayBuffer: ArrayBuffer, filename: string, additionalData: any)
    {
        const apiUrl = this.$store.getters.normalizedCabinetUrl + '/api/mistral/process-ocr';
        const formData = new FormData();

        const blob = new Blob([arrayBuffer]);

        formData.append('file', blob, filename);

        formData.append('additionalData', JSON.stringify(additionalData));

        const response = await this.sharedAxiosInstance!.post(
            apiUrl,
            formData,
            {
                auth: {
                    username: this.getCredentials().username,
                    password: this.getCredentials().password,
                },
                validateStatus: (status) => {
                    return status === 200;
                },
                headers: {'Content-Type': 'multipart/form-data' }
            }
        );

        return response.data;
    }

    async suggestDestinationFolder(fileNames: Array<string>, repertoiresHierarchy: FolderTreeNode[])
    {
        const apiUrl = this.$store.getters.normalizedCabinetUrl + '/api/mistral/suggest-destination-folder';
        const formData = new FormData();

        for(const fileName of fileNames) {
            formData.append('filenames[]', fileName);
        }

        formData.append('cloud-folder-hierarchy', JSON.stringify(repertoiresHierarchy));

        const response = await this.sharedAxiosInstance!.post(
            apiUrl,
            formData,
            {
                auth: {
                    username: this.getCredentials().username,
                    password: this.getCredentials().password,
                },
                validateStatus: (status) => {
                    return status === 200;
                },
                headers: {'Content-Type': 'multipart/form-data' }
            }
        );

        const data = response.data;
        return JSON.parse(data.choices[0].message.content);
    }

    isQuickSignatureCheck(attachment: Office.AttachmentDetails): boolean
    {
        // Quick heuristic for common signature patterns
        return (
            attachment.size < 50000 &&
                attachment.isInline === true &&
                /^image\d{3}\.(png|jpg|jpeg|gif)$/i.test(attachment.name) &&
                attachment.contentType?.startsWith('image/')
        );
    }

}
